import axios from 'axios';
import React, { useContext, useEffect, useState } from 'react';
import Cookies from 'js-cookie';
import UserContext from '../../contexts/UserContext';
import { useLocation, useNavigate, useSearchParams } from 'react-router-dom';
import displayError from '../../utils/ErrorHandler';

const Login = () => {
  const [username, setUsername] = useState('');
  const [password, setPassword] = useState('');
  const [message, setMessage] = useState('');
  const navigate = useNavigate();
  const location = useLocation();
  const { user, setUser } = useContext(UserContext);
  const [searchParams] = useSearchParams();
  const redirectPath = searchParams.get('redirect'); // ✅ Read redirect once

  const handleChange = (e) => {
    if (e.target.name === 'username') setUsername(e.target.value);
    if (e.target.name === 'password') setPassword(e.target.value);
  };

  const submitData = (e) => {
    e.preventDefault();
    if (!username || !password) {
      setMessage('Username & Password is required !');
      return;
    }

    axios
      .post(
        `${process.env.REACT_APP_API_URI}/api/users/login`,
        { username, password },
        { withCredentials: true }
      )
      .then((response) => {
        if (response.data.success) {
          const userData = response?.data?.data;
          const user = userData?.user;
          const token = userData?.token;

          setUser(user);
          // tenant is carried in the returned user object and should be read from context where needed
          Cookies.set('accessToken', token, { expires: 1 });

          // ✅ Redirect to original destination if present
          if (redirectPath) {
            navigate(redirectPath);
            return;
          }

          // ✅ Otherwise fallback to role-based redirects
          switch (user.role) {
            case 'Zone Coordinator':
              navigate('/zc/home');
              break;
            case 'Supplier':
              navigate('/supplier/home', { state: { userData } });
              break;
            case 'Purchase':
            case 'Admin':
            case 'CityAdmin':
              navigate('/central/home');
              break;
            case 'SuperAdmin':
              navigate(user.tenant ? '/central/home' : '/superadmin/home');
              break;
            case 'Silat':
              navigate('/silat/home');
              break;
            case 'Logistics':
              navigate('/logistics/home');
              break;
            case 'Silat Viewer':
              navigate('/silat/commitments');
              break;
            default:
              navigate('/');
          }
        }
      })
      .catch((err) => {
        const errorMessage = displayError(err, navigate, setUser);
        if (errorMessage) {
          setMessage(errorMessage);
        }
      });
  };

  useEffect(() => {
    if (location?.state && typeof location?.state?.message !== 'undefined') {
      setMessage(location.state.message);
      location.state = {};
    } else {
      setMessage('');
    }
  }, [location]);

  return (
    <div className="flex min-h-full flex-1 flex-col justify-center px-6 py-4 lg:px-8">
      {message ? (
        <div
          className="bg-red-100 border border-red-300 text-red-500 px-4 py-3 mx-0 rounded relative"
          role="alert"
        >
          <span className="block sm:inline">{message}</span>
        </div>
      ) : null}

      <div className="lg:mx-auto lg:w-full lg:max-w-lg">
        <img className="mx-auto w-auto" src="fmb_logo.png" alt="Your Company" />
        <h2 className="mt-2 text-center text-xl leading-9 tracking-tight text-gray-900">
          Ashara Mubarak 1448H
        </h2>
      </div>

      <div className="mt-10 sm:mx-auto sm:w-full sm:max-w-sm">
        <form className="space-y-6">
          <div>
            <label htmlFor="username" className="block text-sm font-medium leading-6 text-gray-900">
              Username
            </label>
            <div className="mt-2">
              <input
                id="username"
                name="username"
                type="text"
                placeholder="Username"
                autoComplete="username"
                value={username}
                onChange={handleChange}
                required
                className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <div className="flex items-center justify-between">
              <label htmlFor="password" className="block text-sm font-medium leading-6 text-gray-700">
                Password
              </label>
            </div>
            <div className="mt-2">
              <input
                id="password"
                name="password"
                type="password"
                placeholder="Password"
                autoComplete="current-password"
                value={password}
                onChange={handleChange}
                required
                className="block w-full rounded-full border-0 py-3 px-5 text-gray-900 shadow-sm ring-1 ring-inset ring-gray-300 placeholder:text-gray-400 focus:ring-2 focus:ring-inset focus:ring-indigo-600 sm:text-sm sm:leading-6"
              />
            </div>
          </div>

          <div>
            <button
              type="submit"
              onClick={submitData}
              className="flex w-full justify-center rounded-full text-white bg-fmb px-3 py-3 text-sm font-semibold leading-6 shadow-sm hover:bg-indigo-500 focus-visible:outline focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-indigo-600"
            >
              LOGIN
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};

export default Login;